/**
 * API клиент для получения статистики Instagram медиа
 */

import { getAuthHeaders } from './auth-headers';
import { SUPABASE_FUNCTIONS_URL } from './config';
import { fetchTikTokLinkStats } from './tiktok-link-stats';
import { isTikTokUrl } from '../../../shared/api/tiktok.api';

import type {
  BatchProgress,
  FetchStatsRequest,
  FetchStatsResponse,
  MediaStats,
  MediaStatsError,
} from '../types/statistics';

const BATCH_SIZE = 50;
// Пауза между пачками ссылок. Сетевые сбои и лимиты обрабатываются повтором
// ниже, поэтому большая пауза только растягивала длинные списки (5 с на пачку).
const BASE_DELAY_MS = 500;
const BACKOFF_DELAYS = [30_000, 60_000, 120_000];
const MAX_RATE_LIMIT_RETRIES = 3;
const NETWORK_RETRY_DELAY_MS = 10_000;

class InstagramAPIError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InstagramAPIError';
  }
}

async function fetchMediaStatsBatch(
  urls: string[],
  dateFrom: string | undefined,
  dateTo: string | undefined,
  headers: Record<string, string>,
  signal?: AbortSignal
): Promise<FetchStatsResponse> {
  let response: Response;

  try {
    response = await fetch(`${SUPABASE_FUNCTIONS_URL}/instagram/media-stats`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        urls,
        date_from: dateFrom,
        date_to: dateTo,
      }),
      signal,
    });
  } catch (networkError) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    throw new InstagramAPIError(
      networkError instanceof Error ? networkError.message : 'Не удалось подключиться к серверу'
    );
  }

  // Если функция упала по таймауту или лимиту, Supabase отвечает не JSON.
  const data = await response.json().catch(() => null);

  if (!response.ok || !data) {
    throw new InstagramAPIError(
      data?.error || `Сервер не ответил на пачку ссылок (код ${response.status}) — попробуйте меньше ссылок за раз`
    );
  }

  return data as FetchStatsResponse;
}

function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

function sleepWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

export type BatchedStatsCallbacks = {
  onProgress: (progress: BatchProgress) => void;
  onBatchResults: (results: MediaStats[], errors: MediaStatsError[]) => void;
  onPause: (reason: string, durationMs: number) => void;
  onResume: () => void;
};

export async function fetchMediaStatsBatched(
  request: FetchStatsRequest,
  callbacks: BatchedStatsCallbacks,
  signal?: AbortSignal
): Promise<{ results: MediaStats[]; errors: MediaStatsError[] }> {
  const headers = await getAuthHeaders();

  // TikTok links resolve via the Display API (backend), Instagram links via the
  // edge function. Split so each takes its own path; results merge into one table.
  const tiktokUrls = request.urls.filter(isTikTokUrl);
  const igUrls = request.urls.filter((u) => !isTikTokUrl(u));

  const batches = chunk(igUrls, BATCH_SIZE);
  const totalBatches = batches.length;
  const totalUrls = request.urls.length;

  const resultsMap = new Map<string, MediaStats>();
  const errorsMap = new Map<string, MediaStatsError>();

  let completedUrls = 0;

  if (tiktokUrls.length > 0) {
    const tt = await fetchTikTokLinkStats(tiktokUrls);
    for (const r of tt.results) resultsMap.set(r.shortcode, r);
    for (const e of tt.errors) errorsMap.set(e.url, e);
    completedUrls += tiktokUrls.length;
    callbacks.onBatchResults(tt.results, tt.errors);
    callbacks.onProgress({
      completedUrls: Math.min(completedUrls, totalUrls),
      totalUrls,
      currentBatch: 0,
      totalBatches,
      percent: Math.round(Math.min(completedUrls / totalUrls, 1) * 100),
    });
  }

  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    let urlsForBatch = batches[batchIndex];

    if (batchIndex > 0) {
      await sleepWithAbort(BASE_DELAY_MS, signal);
    }

    let rateLimitRetries = 0;

    while (urlsForBatch.length > 0) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      let batchResponse: FetchStatsResponse;

      try {
        batchResponse = await fetchMediaStatsBatch(
          urlsForBatch,
          request.dateFrom,
          request.dateTo,
          headers,
          signal
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;

        try {
          callbacks.onPause('Сетевая ошибка — повтор через 10 сек...', NETWORK_RETRY_DELAY_MS);
          await sleepWithAbort(NETWORK_RETRY_DELAY_MS, signal);
          callbacks.onResume();

          batchResponse = await fetchMediaStatsBatch(
            urlsForBatch,
            request.dateFrom,
            request.dateTo,
            headers,
            signal
          );
        } catch (retryErr) {
          if (retryErr instanceof DOMException && retryErr.name === 'AbortError') throw retryErr;

          for (const url of urlsForBatch) {
            const errMsg = retryErr instanceof Error ? retryErr.message : 'Сетевая ошибка';
            errorsMap.set(url, { url, error: errMsg });
          }
          completedUrls += urlsForBatch.length;
          break;
        }
      }

      for (const result of batchResponse.results) {
        resultsMap.set(result.shortcode, result);
        errorsMap.delete(result.url);
      }
      for (const error of batchResponse.errors) {
        errorsMap.set(error.url, error);
      }

      callbacks.onBatchResults(batchResponse.results, batchResponse.errors);

      const processedInBatch = batchResponse.results.length + batchResponse.errors.length;
      completedUrls += processedInBatch;

      callbacks.onProgress({
        completedUrls: Math.min(completedUrls, totalUrls),
        totalUrls,
        currentBatch: batchIndex + 1,
        totalBatches,
        percent: Math.round(Math.min(completedUrls / totalUrls, 1) * 100),
      });

      if (batchResponse.rateLimited && batchResponse.unprocessedUrls.length > 0) {
        if (rateLimitRetries >= MAX_RATE_LIMIT_RETRIES) {
          for (const url of batchResponse.unprocessedUrls) {
            errorsMap.set(url, {
              url,
              error: 'Rate limit exceeded — превышен лимит API Instagram',
            });
          }
          completedUrls += batchResponse.unprocessedUrls.length;
          break;
        }

        const delayMs = BACKOFF_DELAYS[Math.min(rateLimitRetries, BACKOFF_DELAYS.length - 1)];
        rateLimitRetries++;

        callbacks.onPause(
          `Rate limit — пауза ${Math.round(delayMs / 1000)} сек (попытка ${rateLimitRetries}/${MAX_RATE_LIMIT_RETRIES})...`,
          delayMs
        );
        await sleepWithAbort(delayMs, signal);
        callbacks.onResume();

        urlsForBatch = batchResponse.unprocessedUrls;
        continue;
      }

      break;
    }
  }

  return {
    results: Array.from(resultsMap.values()),
    errors: Array.from(errorsMap.values()),
  };
}

export async function fetchMediaStats(request: FetchStatsRequest): Promise<FetchStatsResponse> {
  const headers = await getAuthHeaders();
  return fetchMediaStatsBatch(request.urls, request.dateFrom, request.dateTo, headers);
}
